import { Box, Button, Chip, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import LoginSideImage from '../../images/login/login-side-register.png';
import LogoImage from '../../images/logo/logo.png';
import { APP_NAME } from '../../constants/brand';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import Fetch from "../../services/Fetch";
import dayjs from "dayjs";
import SnackbarAlert from "../../components/SnackBar";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { buildRegisterFormData } from "../../helper/RegisterFormData";
import { useConstants } from "../../hooks/UseConstants";

const fieldSx = {
     '& .MuiOutlinedInput-root': {
          borderRadius: 2.5,
          bgcolor: 'background.paper',
     },
};

function Register() {
     const { host, language } = useConstants();
     const isAr = language === "ar";
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { sendWait, setSendWait } = useWaits();
     const [firstName, setFirstName] = useState('');
     const [lastName, setLastName] = useState('');
     const [email, setEmail] = useState('');
     const [phoneCode, setPhoneCode] = useState('');
     const [phoneNumber, setPhoneNumber] = useState('');
     const [birthDate, setBirthDate] = useState('');
     const [password, setPassword] = useState('');
     const [passwordConfirmation, setPasswordConfirmation] = useState('');
     const navigate = useNavigate();

     const handleChange = (value, country) => {
          const numberWithoutCode = value.replace(country.dialCode, "");
          setPhoneCode(country.dialCode);
          setPhoneNumber(numberWithoutCode);
     };

     const register = async () => {
          if (!firstName.trim() || !lastName.trim() || !email.trim() || !password || !passwordConfirmation) {
               setSnackBar("error", isAr ? "أكمل الحقول الأساسية المطلوبة" : "Complete the required fields");
               return;
          }
          if (password !== passwordConfirmation) {
               setSnackBar("error", isAr ? "كلمتا المرور غير متطابقتين" : "Passwords do not match");
               return;
          }

          setSendWait(true);

          const formData = buildRegisterFormData({
               firstName: firstName.trim(),
               lastName: lastName.trim(),
               phoneCode,
               phoneNumber,
               birthDate,
               email: email.trim(),
               password,
               passwordConfirmation,
               accountRole: "teacher",
          });

          const result = await Fetch(`${host}/register`, 'POST', formData);

          if (result.status === 200) {
               localStorage.setItem('token', result.data.data.token);
               localStorage.setItem('language', result.data.data.user?.language || language || "ar");
               if (result.data.data.user?.id) {
                    localStorage.setItem('id', result.data.data.user.id);
               }
               navigate('/profile');
          } else if (result.status === 422) {
               setSnackBar("error", result.data?.errors?.[0] || result.data?.message);
          } else {
               setSnackBar("error", isAr ? "تعذر إنشاء الحساب" : "Unable to create account");
          }

          setSendWait(false);
     };

     return (
          <>
               {wait ? (
                    <Box className="w-full h-screen relative flex justify-center items-center">
                         <CircularProgress size={70} />
                    </Box>
               ) : (
                    <Box
                         sx={{
                              minHeight: '100vh',
                              background: (theme) => theme.palette.mode === 'dark'
                                   ? theme.palette.background.default
                                   : 'linear-gradient(180deg, #eef4ff 0%, #f8fbff 100%)',
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', lg: '1fr minmax(520px, 620px)' },
                         }}
                    >
                         <Box
                              sx={{
                                   display: { xs: 'none', lg: 'block' },
                                   position: 'relative',
                                   overflow: 'hidden',
                              }}
                         >
                              <Box
                                   component="img"
                                   src={LoginSideImage}
                                   alt={isAr ? "واجهة إنشاء حساب المدرس" : "Teacher registration illustration"}
                                   sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                   }}
                              />
                              <Box
                                   sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(145deg, rgba(13,53,140,0.82) 0%, rgba(29,78,216,0.48) 45%, rgba(15,23,42,0.62) 100%)',
                                   }}
                              />
                              <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', p: 6 }}>
                                   <Box sx={{ maxWidth: 460, color: 'white' }}>
                                        <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.15, mb: 2 }}>
                                             {isAr ? "ابدأ منصتك التعليمية بثقة" : "Start your teaching platform with confidence"}
                                        </Typography>
                                        <Typography sx={{ opacity: 0.92, lineHeight: 1.9 }}>
                                             {isAr
                                                  ? "أنشئ حساب المدرس لإضافة الدورات وإدارة المحتوى ومتابعة أرباحك من مكان واحد بتصميم أوضح وأسهل."
                                                  : "Create your teacher account to publish courses, manage content, and track your earnings from one place."}
                                        </Typography>
                                   </Box>
                              </Box>
                         </Box>

                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
                              <Paper
                                   elevation={0}
                                   sx={{
                                        width: '100%',
                                        maxWidth: 620,
                                        borderRadius: { xs: 4, md: '40px 0 0 40px' },
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'rgba(148,163,184,0.22)',
                                        bgcolor: 'background.paper',
                                        boxShadow: '0 24px 60px rgba(15,23,42,0.12)',
                                   }}
                              >
                                   <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                             <Chip
                                                  label={isAr ? "حساب مدرس جديد" : "New teacher account"}
                                                  sx={{ bgcolor: 'rgba(13,53,140,0.1)', color: '#0D358C', fontWeight: 700 }}
                                             />
                                             <Box component="img" src={LogoImage} alt={APP_NAME} sx={{ width: 'auto', maxWidth: 100, maxHeight: 40, objectFit: 'contain' }} />
                                        </Box>

                                        <Box>
                                             <Typography variant="h4" fontWeight={900} sx={{ color: '#0D358C', lineHeight: 1.15, fontSize: { xs: '1.9rem', sm: '2.25rem' } }}>
                                                  {isAr ? "إنشاء حساب مدرس" : "Create teacher account"}
                                             </Typography>
                                             <Typography variant="body1" sx={{ mt: 1.25, color: 'text.secondary', lineHeight: 1.8 }}>
                                                  {isAr
                                                       ? "أنشئ حسابك لبدء إضافة الدورات وإدارة المحتوى والأرباح من مكان واحد."
                                                       : "Create your account to start publishing courses, managing content, and tracking earnings."}
                                             </Typography>
                                        </Box>

                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                             <TextField
                                                  value={lastName}
                                                  onChange={(e) => setLastName(e.target.value)}
                                                  label={isAr ? "الاسم الأخير" : "Last name"}
                                                  fullWidth
                                                  sx={fieldSx}
                                             />
                                             <TextField
                                                  value={firstName}
                                                  onChange={(e) => setFirstName(e.target.value)}
                                                  label={isAr ? "الاسم الأول" : "First name"}
                                                  fullWidth
                                                  sx={fieldSx}
                                             />
                                        </Box>

                                        <Box sx={{ display: 'grid', gap: 2.5 }}>
                                             <Box>
                                                  <Typography variant="subtitle2" sx={{ mb: 1.25, color: '#0D358C', fontWeight: 800 }}>
                                                       {isAr ? "بيانات التواصل" : "Contact details"}
                                                  </Typography>
                                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                                                       <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                                                                 {isAr ? "رقم الهاتف" : "Phone number"}
                                                            </Typography>
                                                            <PhoneInput
                                                                 country={isAr ? 'eg' : 'us'}
                                                                 value={`${phoneCode || ''}${phoneNumber || ''}`}
                                                                 onChange={handleChange}
                                                                 containerStyle={{ width: '100%' }}
                                                                 inputStyle={{
                                                                      width: '100%',
                                                                      height: '56px',
                                                                      borderRadius: '12px',
                                                                      borderColor: '#cbd5e1',
                                                                      paddingLeft: '52px',
                                                                 }}
                                                                 buttonStyle={{
                                                                      borderTopLeftRadius: '12px',
                                                                      borderBottomLeftRadius: '12px',
                                                                      borderColor: '#cbd5e1',
                                                                 }}
                                                            />
                                                       </Box>

                                                       <Box sx={{ mt: { xs: 0, sm: 1 } }}>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                 <DatePicker
                                                                      value={birthDate ? dayjs(birthDate) : null}
                                                                      onChange={(value) => setBirthDate(value ? dayjs(value).format("YYYY-MM-DD") : "")}
                                                                      label={isAr ? "تاريخ الميلاد" : "Birth date"}
                                                                      slotProps={{ textField: { fullWidth: true, sx: fieldSx } }}
                                                                 />
                                                            </LocalizationProvider>
                                                       </Box>

                                                       <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                                                            <TextField
                                                                 value={email}
                                                                 onChange={(e) => setEmail(e.target.value)}
                                                                 label={isAr ? "البريد الإلكتروني" : "Email"}
                                                                 type="email"
                                                                 fullWidth
                                                                 sx={fieldSx}
                                                            />
                                                       </Box>
                                                  </Box>
                                             </Box>

                                             <Box>
                                                  <Typography variant="subtitle2" sx={{ mb: 1.25, color: '#0D358C', fontWeight: 800 }}>
                                                       {isAr ? "بيانات الحساب" : "Account details"}
                                                  </Typography>
                                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                                       <TextField
                                                            value={passwordConfirmation}
                                                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                                                            label={isAr ? "تأكيد كلمة المرور" : "Confirm password"}
                                                            type="password"
                                                            fullWidth
                                                            sx={fieldSx}
                                                       />
                                                       <TextField
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                            label={isAr ? "كلمة المرور" : "Password"}
                                                            type="password"
                                                            fullWidth
                                                            sx={fieldSx}
                                                       />
                                                  </Box>
                                             </Box>
                                        </Box>

                                        <Button
                                             variant="contained"
                                             size="large"
                                             onClick={register}
                                             disabled={sendWait}
                                             sx={{
                                                  mt: 0.5,
                                                  minHeight: 52,
                                                  borderRadius: 3,
                                                  bgcolor: '#0D358C',
                                                  fontWeight: 800,
                                                  boxShadow: '0 12px 30px rgba(13,53,140,0.25)',
                                                  '&:hover': { bgcolor: '#1d4ed8' },
                                             }}
                                        >
                                             {sendWait ? <CircularProgress size={22} color="inherit" /> : (isAr ? "إنشاء الحساب" : "Create account")}
                                        </Button>

                                        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: '#e2e8f0' }}>
                                             <Typography
                                                  variant="body2"
                                                  sx={{ color: '#0D358C', cursor: 'pointer', fontWeight: 700 }}
                                                  onClick={() => navigate('/login')}
                                             >
                                                  {isAr ? "لديك حساب بالفعل؟ سجل الدخول" : "Already have an account? Sign in"}
                                             </Typography>
                                        </Box>
                                   </Box>
                              </Paper>
                         </Box>
                    </Box>
               )}
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default Register;